import EventDispatcher from "../../../../../shared/lib/html5-software-library/src/ranj/util/EventDispatcher";
import AppSignalService from "../../../../../shared/src/ranj/app-signal/AppSignalService";
import ProgressValue from "../../../../../shared/src/ranj/equalture/platform/ProgressValue";
import SaveEvent from "../../../../../shared/src/ranj/equalture/savegame/SaveEvent";
import GameConfig from "./GameConfig";
import GameStatus from "./GameStatus";
import TrackingController from "./TrackingController";
import Config from "../../../../../shared/src/ranj/app/config/Config";

export default class GameModel extends EventDispatcher {

    /**
     * @param {GameConfig} gameConfig 
     * @param {TrackingController} trackingController 
     * @param {AppSignalService} appSignalService 
     * @param {Config} config 
     */
    constructor(dispatcher, gameConfig, trackingController, appSignalService, config) {
        super();
        this._contextDispatcher = dispatcher;
        this._gameConfig = gameConfig;
        this._trackingController = trackingController;
        this._appSignalService = appSignalService;
        this._config = config;
    }

    dispatchPlatformProgressEvent(progressValue) {
        this._trackingController.trackProgress(progressValue, this.elapsedTime);
    }

    initialize() {
        this._sectionIndex = -1;
        this.speed = 0;
        //Store often used config values        
        this._acceleration = this._gameConfig.getValue("acceleration");
        this._graceEarly = this._gameConfig.getValue("graceBeforeSectionChange");
        this._graceLate = this._gameConfig.getValue("graceAfterSectionChange");
        this._carOffset = this._gameConfig.getValue("carOffsetY");
        this._sectionLength = this._gameConfig.getValue("sectionLength");

        this._zeroMovesWarningSent = false;
    }

    reset() {
        this._sectionIndex = -1;
        this.offset = this._gameConfig.getValue("start.offset");
        this.moves = 0;
        this.speed = 0;
        this.elapsedTime = 0;
        this.lane = this._gameConfig.getValue("start.laneIndex");

        this._zeroMovesWarningSent = false;
    }

    set restoredFeedbackPending(bool) {
        this._restoredFeedbackPending = bool;
    }
    get restoredFeedbackPending() {
        return this._restoredFeedbackPending;
    }

    startTutorial() {
        this.status = GameStatus.TUTORIAL;
    }

    start() {
        this._rulesChanged = false;
        this._justStarted = true;
        this.dispatchPlatformProgressEvent(ProgressValue.STARTED);
        this.status = GameStatus.RUNNING;
    }

    end() {
        this.status = GameStatus.END;
        this.dispatchPlatformProgressEvent(ProgressValue.COMPLETED);
    }

    set offset(value) {
        this._offset = value;
        this.dispatchEvent(GameModel.OFFSET, this._offset);
        this.dispatchEvent(GameModel.PROGRESS, this._offset / this._gameConfig.roadLength);

    }
    get offset() {
        return this._offset;
    }

    set speed(value) {
        this._speed = value;

        let dispatchedSpeed = this._speed;
        if (this._overrideSpeed !== null && this._overrideSpeed !== undefined) {
            dispatchedSpeed = this._overrideSpeed;
        }

        this.dispatchEvent(GameModel.SPEED, dispatchedSpeed);
    }
    get speed() {
        return this._speed;
    }

    set elapsedTime(value) {
        this._elapsedTime = value;
        this.dispatchEvent(GameModel.ELAPSED_TIME, this._elapsedTime);

        if (this._config.getValue('errorTracking.enabled')) {
            if (this._elapsedTime > 60000 && this.moves <= 0 && !this._zeroMovesWarningSent) {
                this._zeroMovesWarningSent = true;
    
                console.log('Sending 0 moves warning to AppSignal...')
                this._appSignalService.client.sendError(new Error("User has made 0 moves"), (span) => {
                    span.setAction('User has made 0 moves after 1 minute');
                    span.setTags({
                        log_level: "warning",
                        gamemodel_status: this.status,
                    });
                })
            }
        }
    }
    get elapsedTime() {
        return this._elapsedTime;
    }

    set moves(value) {
        this._moves = value;
        this.dispatchEvent(GameModel.MOVES, this._moves);
    }
    /**
     * @type {Number}
     */
    get moves() {
        return this._moves;
    }

    set lane(value) {
        if (this.lane != value && this.status == GameStatus.RUNNING) {
            this._justMoved = true;
            this.moves++;
        }
        this._lane = value;
        this.dispatchEvent(GameModel.LANE, this._lane);
    }
    get lane() {
        return this._lane;
    }

    set status(value) {
        this._status = value;
        this.dispatchEvent(GameModel.STATUS, value);

        console.log(`GameModel.status `, value);
    }
    get status() {
        return this._status;
    }

    /**
     * Debug/test method
     */
    switchToFastestLane() {
        this.lane = this._gameConfig.getRoadDataForPosition(this.offset).fastLane;
    }

    step(delta) {
        let speed = this.speed;
        const lane = this._lane;
        const offset = this._offset + this._carOffset;//use front axle of car
        let acceleration = this._acceleration;
        switch (this._status) {

            case GameStatus.RUNNING:
                const roadData = this._gameConfig.getRoadDataForPosition(offset);
                const laneData = this._gameConfig.getLaneDataForPosition(offset, lane);
                //Section change?
                if (roadData.sectionIndex != this._sectionIndex) {
                    this._sectionIndex = roadData.sectionIndex;
                    this._rulesChanged = false;
                    if (roadData.action) {
                        this.executeAction(roadData.action);
                    }
                    if (!this._justStarted) {
                        this._trackingController.trackSectionChange(offset, this._rulesChanged, roadData.fastLane, roadData.speedMaxIndex, this.elapsedTime);
                    }
                    this._lastSectionChange = offset;
                    //'late' grace applies only when user has not changed lanes within early grace period
                    this._lateGraceApplies = this._speedUntilNextSection == null;
                    //Reset 'early' grace:
                    this._speedUntilNextSection = null;
                    //Save
                    this._contextDispatcher.dispatch(SaveEvent.SAVE);
                }
                const sectionProgress = roadData.sectionProgress;//in absolute value (pixels)
                //Check if player switched lanes around section transition. If so, keep original speed.
                //'Early' grace: If user moved early within grace period : use speed of lane in next section
                //'Late' grace: After section change, keep using speed of previous section until player moves lane or until grace period expires.
                //Start with default: speed of current lane, and change according to grace 
                speed = laneData.speed;
                const earlyGraceApplies = this._sectionLength - sectionProgress < this._graceEarly;

                if (this._justMoved || this._justStarted) {
                    //A move by the player cancels the 'late' grace period
                    this._lateGraceApplies = false;
                    //Check if move is within 'early' grace period                    
                    //console.log("Check early grace: ", this._sectionLength - sectionProgress, this._graceEarly);
                    if (earlyGraceApplies) {
                        //Apply 'early' grace: use speed of next section (back to normal when we reach next section)
                        this._speedUntilNextSection = this._gameConfig.getSpeedForPosition(offset + this._graceEarly, lane);
                    }
                    //Track lane switch: offset, new lane, new speed, lane color (index), correctness
                    const speedMaxIndex = this._justStarted ? this._gameConfig.speedMaxIndex : roadData.speedMaxIndex;
                    let correct = laneData.speedIndex == speedMaxIndex;
                    this._trackingController.trackLaneSwitch(offset, lane, laneData.speedIndex, laneData.color, laneData.shape, correct, this.elapsedTime);
                }
                //Reset late grace?
                if (offset - this._lastSectionChange > this._graceLate) {
                    this._lateGraceApplies = false;
                }
                //Determine speed
                if (this._lateGraceApplies) {
                    //Still in 'late' grace period (and lane not switched yet): use speed of previous section
                    speed = this._gameConfig.getSpeedForPosition(offset - this._graceLate, lane);
                } else if (this._speedUntilNextSection != null) {
                    //Still in 'early' grace period and user has moved within early grace period: use speed of next section
                    speed = this._speedUntilNextSection;
                }

                this._justStarted = false;
                this.elapsedTime += delta;
                break;

            case GameStatus.TUTORIAL:
                break;

            case GameStatus.FINISH:
                if (Date.now() - this._finishTime >= this._gameConfig.getValue("finish.delayBeforeEnd")) {
                    this.end();
                } else {
                    acceleration = this._deceleration;
                    speed = Math.max(0, this.speed - acceleration);
                }
                break;
        }

        this._justMoved = false;

        const dspeed = speed - this.speed;
        if (dspeed != 0) {
            const sgn = dspeed > 0 ? 1 : -1;
            this.speed += sgn * Math.min(acceleration, Math.abs(dspeed));
        } else {
            this.speed = speed;
        }

        if (this._overrideSpeed !== null && this._overrideSpeed !== undefined) {
            this.offset += this._overrideSpeed * (delta / 16.66666667);
        } else {
            this.offset += this._speed * (delta / 16.66666667);
        }

        // Prevent the player from driving outside of the tutorial zone
        if (this._wrapOffsetAt !== undefined && this._wrapOffsetAt !== null && this.offset >= this._wrapOffsetAt) {
            this.offset = this._wrapOffsetTo;
        }

        if (this._status === GameStatus.TUTORIAL) {
            // Keep car before start line - found by logging GameModel.offset during the game
            if (this.offset >= 10200) {
                this.offset = 10200;
            }
        }
    }

    /**
     * Wraps the offset when above 'at' to 'to' (useful for keeper the player/ car in the tutorial area)
     * @param {number} at 
     * @param {number} to 
     */
    wrapOffsetAtTo(at, to) {
        this._wrapOffsetTo = to;
        this._wrapOffsetAt = at;
    }

    removeOffsetWrapping() {
        this._wrapOffsetTo = null;
        this._wrapOffsetAt = null;
    }

    set overrideSpeed(value) {
        this._overrideSpeed = value;
    }

    removeSpeedOverride() {
        this._overrideSpeed = null;
    }


    /**
     * 
     * @param {String} action - label for action
     */
    executeAction(action) {
        console.log(`executeAction '${action}'`);
        switch (action) {
            case "start":
                break;
            case "finish":
                this._finishTime = Date.now();
                this.status = GameStatus.FINISH;
                this._deceleration = this._speed / this._gameConfig.getValue("finish.decelerationDuration");
                break;

            case "rulechange":
                this._rulesChanged = true;
                break;
        }
    }



    getSnapshot() {
        return {
            status: this.status,
            offset: this.offset,
            elapsedTime: Math.round(this.elapsedTime),
            moves: this.moves,
            lane: this.lane
        };
    }

    applySnapshot(obj, ignoreStatus = false) {
        this.restoredFeedbackPending = true;
        this.lane = obj.lane;
        this.moves = obj.moves;
        this.elapsedTime = obj.elapsedTime;
        if (!ignoreStatus) {
            this.status = obj.status;
        } else {
            this.status = GameStatus.NONE;
        }
        this.offset = obj.offset;
        const sectionIndex = this._gameConfig.getRoadDataForPosition(obj.offset + this._carOffset).sectionIndex;
        if (sectionIndex > 1) {
            this.offset = sectionIndex * this._gameConfig.getValue("sectionLength") - this._gameConfig.getValue("savegame.offsetOnRestore");
        } else {
            this.offset = this._gameConfig.getValue("start.offset");
        }
    }
};


GameModel.STATUS = "GameModel.STATUS";
GameModel.OFFSET = "GameModel.OFFSET";
GameModel.PROGRESS = "GameModel.PROGRESS";
GameModel.MOVES = "GameModel.MOVE";
GameModel.ELAPSED_TIME = "GameModel.TIME";
GameModel.LANE = "GameModel.LANE";
GameModel.SPEED = "GameModel.SPEED";